import React, { useState, useEffect } from "react";
import { fetchCourseOrderData, CourseMetadataItem } from "../API/Course/CourseAPI";
import { dataLoaderUri, mobileAppUri } from "../API/API";
import apiClient from "../services/apiClient";
import BackButton from "../shared/BackButton/BackButton";
import styles from "./CourseCustomization.module.css";

interface CourseConfig {
  sectionId: string;
  disabledComponents: string[];
  targetLanguage: string;
  domain: string;
  notes?: string;
}

const AVAILABLE_COMPONENTS = [
  "Flashcards",
  "Matching",
  "Memory",
  "Fill in the blank",
  "Multiple Choice",
  "Pronunciation",
  "SpeedGame",
  "Passage",
  "Photolist",
  "Sentence Translation Target",
  "Sentence Translation Source",
  "Item Guess",
  "Conversation",
  "ConversationResponse",
  "Item Describe",
  "Reading Comprehension Base",
  "Reading Comprehension Target",
  "Listening Comprehension Base",
  "Listening Comprehension Target"
];

const LANGUAGES = [
  { code: "EN", name: "English" },
  { code: "ES", name: "Spanish" },
  { code: "FR", name: "French" },
  { code: "DE", name: "German" },
  { code: "IT", name: "Italian" },
  { code: "PT", name: "Portuguese" }
];

const CourseCustomization: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("ES");
  const [courseOrder, setCourseOrder] = useState<CourseMetadataItem[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
  const [courseConfigs, setCourseConfigs] = useState<{ [key: string]: CourseConfig }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  // Bulk configuration mode state
  const [bulkMode, setBulkMode] = useState<boolean>(false);
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [bulkConfig, setBulkConfig] = useState<{
    disabledComponents: string[];
    domain: string;
    notes: string;
  }>({
    disabledComponents: [],
    domain: "General",
    notes: ""
  });

  // Fetch course order when language changes
  useEffect(() => {
    if (selectedLanguage) {
      fetchCourseOrder();
    }
  }, [selectedLanguage]);

  // Fetch existing configurations when course order changes
  useEffect(() => {
    if (courseOrder.length > 0) {
      // Try to fetch existing configurations, but don't fail if it doesn't work
      fetchExistingConfigurations().catch(error => {
        console.log("Could not fetch existing configurations (this is okay for new setups):", error);
        // Continue with empty configurations
      });
    }
  }, [courseOrder]);

  const fetchCourseOrder = async () => {
    setLoading(true);
    try {
      const rawData = await fetchCourseOrderData(selectedLanguage);
      // Sort sections by identifier numerically (same as Fill in the Blank manager)
      const sortedData = rawData.sort((a, b) => +a.Identifier - +b.Identifier);
      setCourseOrder(sortedData);
      setCurrentSectionIndex(0);
      setMessage(`Loaded ${sortedData.length} sections for ${selectedLanguage}`);
    } catch (error) {
      console.error("Error fetching course order:", error);
      setMessage("Error loading course sections");
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingConfigurations = async () => {
    try {
      // Fetch all existing configurations from mobile app URI with target language
      const data = await apiClient.get(
        `${mobileAppUri}/api/v1/course-config`,
        { params: { targetLanguage: selectedLanguage } }
      );
      if (data) {
        const configMap: { [key: string]: CourseConfig } = {};
        
        data.sections?.forEach((section: any) => {
          configMap[section.sectionId] = {
            sectionId: section.sectionId,
            disabledComponents: section.disabledComponents || [],
            targetLanguage: section.targetLanguage || selectedLanguage,
            domain: section.domain || "General",
            notes: section.notes || ""
          };
        });
        
        setCourseConfigs(configMap);
      }
    } catch (error) {
      console.error("Error fetching existing configurations:", error);
    }
  };

  const getCurrentSection = (): CourseMetadataItem | null => {
    return courseOrder[currentSectionIndex] || null;
  };

  const getCurrentConfig = (): CourseConfig => {
    const currentSection = getCurrentSection();
    if (!currentSection) {
      return {
        sectionId: "",
        disabledComponents: [],
        targetLanguage: selectedLanguage,
        domain: "General"
      };
    }

    return courseConfigs[currentSection.Vocabulary_List] || {
      sectionId: currentSection.Vocabulary_List,
      disabledComponents: [],
      targetLanguage: selectedLanguage,
      domain: "General"
    };
  };

  const updateCurrentConfig = (updates: Partial<CourseConfig>) => {
    const currentSection = getCurrentSection();
    if (!currentSection) return;

    const sectionId = currentSection.Vocabulary_List;
    setCourseConfigs(prev => ({
      ...prev,
      [sectionId]: {
        ...getCurrentConfig(),
        ...updates,
        sectionId
      }
    }));
  };

  const toggleComponent = (component: string) => {
    const currentConfig = getCurrentConfig();
    const isDisabled = currentConfig.disabledComponents.includes(component);
    
    const newDisabledComponents = isDisabled
      ? currentConfig.disabledComponents.filter(c => c !== component)
      : [...currentConfig.disabledComponents, component];

    updateCurrentConfig({ disabledComponents: newDisabledComponents });
  };

  const saveCurrentConfiguration = async () => {
    const currentSection = getCurrentSection();
    const currentConfig = getCurrentConfig();
    
    if (!currentSection || !currentConfig.sectionId) return;

    setSaving(true);
    try {
      const payload = {
        SectionId: currentConfig.sectionId,
        DisabledComponents: currentConfig.disabledComponents,
        Targ_Lang_Code: selectedLanguage,
        Domain: currentConfig.domain,
        Notes: currentConfig.notes || "",
        UpdatedBy: "web-admin"
      };

      // Check if we have an existing config in our local state first
      const configExists = courseConfigs[currentConfig.sectionId] && 
                          courseConfigs[currentConfig.sectionId].sectionId === currentConfig.sectionId;

      // Choose method and URL based on whether config exists locally
      const method = configExists ? "PUT" : "POST";
      const url = configExists 
        ? `${dataLoaderUri}/api/v1/course-config/${currentConfig.sectionId}`
        : `${dataLoaderUri}/api/v1/course-config`;

      console.log(`Saving configuration with ${method} to ${url}`, payload);

      try {
        let responseData;
        if (configExists) {
          responseData = await apiClient.put(url, payload);
        } else {
          responseData = await apiClient.post(url, payload);
        }
        
        console.log("Save response:", responseData);
        setMessage(`✅ Configuration saved for ${currentConfig.sectionId} (${method})`);
        
        // Update local state instead of fetching from server to avoid CORS issues
        setCourseConfigs(prev => ({
          ...prev,
          [currentConfig.sectionId]: currentConfig
        }));
      } catch (error: any) {
        // If POST fails with 400, try PUT (config might exist on server but not locally)
        if (method === "POST" && error.response?.status === 400) {
          console.log("POST failed, trying PUT instead...");
          try {
            const putResponse = await apiClient.put(
              `${dataLoaderUri}/api/v1/course-config/${currentConfig.sectionId}`,
              payload
            );
            
            console.log("PUT response:", putResponse);
            setMessage(`✅ Configuration updated for ${currentConfig.sectionId} (PUT)`);
            
            // Update local state instead of fetching from server
            setCourseConfigs(prev => ({
              ...prev,
              [currentConfig.sectionId]: currentConfig
            }));
          } catch (putError: any) {
            const errorMessage = putError.response?.data?.error || putError.response?.data?.message || putError.message || 'Unknown error';
            setMessage(`❌ Error saving: ${errorMessage}`);
          }
        } else {
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
          setMessage(`❌ Error saving: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      setMessage(`❌ Error saving configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const navigateSection = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    } else if (direction === 'next' && currentSectionIndex < courseOrder.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  // Bulk configuration functions
  const toggleSectionSelection = (sectionId: string) => {
    setSelectedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const selectAllSections = () => {
    const allSectionIds = courseOrder.map((s, index) =>
      s.Vocabulary_List || `section-${s.Identifier}-${index}`
    );
    setSelectedSections(new Set(allSectionIds));
  };

  const clearSectionSelection = () => {
    setSelectedSections(new Set());
  };

  const toggleBulkComponent = (component: string) => {
    setBulkConfig(prev => {
      const isDisabled = prev.disabledComponents.includes(component);
      return {
        ...prev,
        disabledComponents: isDisabled
          ? prev.disabledComponents.filter(c => c !== component)
          : [...prev.disabledComponents, component]
      };
    });
  };

  const saveBulkConfiguration = async () => {
    if (selectedSections.size === 0) {
      setMessage("❌ Please select at least one section");
      return;
    }

    setSaving(true);
    let successCount = 0;
    let failCount = 0;

    for (const sectionId of selectedSections) {
      try {
        const payload = {
          SectionId: sectionId,
          DisabledComponents: bulkConfig.disabledComponents,
          Targ_Lang_Code: selectedLanguage,
          Domain: bulkConfig.domain,
          Notes: bulkConfig.notes || "",
          UpdatedBy: "web-admin-bulk"
        };

        // Check if config exists
        const configExists = courseConfigs[sectionId] && courseConfigs[sectionId].sectionId === sectionId;

        try {
          if (configExists) {
            await apiClient.put(`${dataLoaderUri}/api/v1/course-config/${sectionId}`, payload);
          } else {
            await apiClient.post(`${dataLoaderUri}/api/v1/course-config`, payload);
          }
          successCount++;

          // Update local state
          setCourseConfigs(prev => ({
            ...prev,
            [sectionId]: {
              sectionId,
              disabledComponents: bulkConfig.disabledComponents,
              targetLanguage: selectedLanguage,
              domain: bulkConfig.domain,
              notes: bulkConfig.notes
            }
          }));
        } catch (error: any) {
          // If POST fails, try PUT
          if (!configExists && error.response?.status === 400) {
            try {
              await apiClient.put(`${dataLoaderUri}/api/v1/course-config/${sectionId}`, payload);
              successCount++;
              setCourseConfigs(prev => ({
                ...prev,
                [sectionId]: {
                  sectionId,
                  disabledComponents: bulkConfig.disabledComponents,
                  targetLanguage: selectedLanguage,
                  domain: bulkConfig.domain,
                  notes: bulkConfig.notes
                }
              }));
            } catch {
              failCount++;
            }
          } else {
            failCount++;
          }
        }
      } catch (error) {
        failCount++;
      }
    }

    setSaving(false);
    if (failCount === 0) {
      setMessage(`✅ Successfully saved configuration for ${successCount} sections`);
    } else {
      setMessage(`⚠️ Saved ${successCount} sections, ${failCount} failed`);
    }
  };

  const currentSection = getCurrentSection();
  const currentConfig = getCurrentConfig();

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Course Customization</h1>
      
      {/* Language Selection */}
      <div className={styles.languageSection}>
        <label className={styles.label}>Select Language:</label>
        <select
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          className={styles.select}
        >
          {LANGUAGES.map(lang => (
            <option key={lang.code} value={lang.code}>{lang.name}</option>
          ))}
        </select>

        <div className={styles.modeToggle}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={bulkMode}
              onChange={(e) => {
                setBulkMode(e.target.checked);
                if (!e.target.checked) {
                  setSelectedSections(new Set());
                }
              }}
            />
            <span>Bulk Configuration Mode</span>
          </label>
        </div>
      </div>

      {loading && <div className={styles.loading}>Loading course sections...</div>}
      
      {message && (
        <div className={`${styles.message} ${message.includes('❌') ? styles.error : styles.success}`}>
          {message}
        </div>
      )}

      {courseOrder.length > 0 && !bulkMode && currentSection && (
        <div className={styles.sectionContainer}>
          {/* Section Navigation */}
          <div className={styles.navigation}>
            <button
              onClick={() => navigateSection('prev')}
              disabled={currentSectionIndex === 0}
              className={styles.navButton}
            >
              ← Previous
            </button>

            <div className={styles.sectionInfo}>
              <h2>Section {currentSectionIndex + 1} of {courseOrder.length}</h2>
              <h3>{currentSection.Vocabulary_List}</h3>
              <p>Course: {currentSection.Identifier}</p>
            </div>

            <button
              onClick={() => navigateSection('next')}
              disabled={currentSectionIndex === courseOrder.length - 1}
              className={styles.navButton}
            >
              Next →
            </button>
          </div>

          {/* Configuration Options */}
          <div className={styles.configSection}>
            <h3>Component Configuration</h3>

            {/* Domain Selection */}
            <div className={styles.domainSection}>
              <label className={styles.label}>Domain:</label>
              <select
                value={currentConfig.domain}
                onChange={(e) => updateCurrentConfig({ domain: e.target.value })}
                className={styles.select}
              >
                <option value="General">General</option>
                <option value="Business">Business</option>
                <option value="Medical">Medical</option>
                <option value="Academic">Academic</option>
                <option value="Travel">Travel</option>
              </select>
            </div>

            {/* Component Toggle Grid */}
            <div className={styles.componentGrid}>
              <h4>Disable/Enable Components:</h4>
              <div className={styles.grid}>
                {AVAILABLE_COMPONENTS.map(component => (
                  <div key={component} className={styles.componentItem}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={currentConfig.disabledComponents.includes(component)}
                        onChange={() => toggleComponent(component)}
                      />
                      <span className={currentConfig.disabledComponents.includes(component) ? styles.disabled : styles.enabled}>
                        {component} {currentConfig.disabledComponents.includes(component) ? '(Disabled)' : '(Enabled)'}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes Section */}
            <div className={styles.notesSection}>
              <label className={styles.label}>Notes:</label>
              <textarea
                value={currentConfig.notes || ""}
                onChange={(e) => updateCurrentConfig({ notes: e.target.value })}
                className={styles.textarea}
                placeholder="Add any notes about this section configuration..."
                rows={3}
              />
            </div>

            {/* Save Button */}
            <button
              onClick={saveCurrentConfiguration}
              disabled={saving}
              className={styles.saveButton}
            >
              {saving ? "Saving..." : "Save Configuration"}
            </button>
          </div>

          {/* Configuration Summary */}
          <div className={styles.summary}>
            <h4>Current Configuration Summary:</h4>
            <p><strong>Section:</strong> {currentConfig.sectionId}</p>
            <p><strong>Language:</strong> {selectedLanguage}</p>
            <p><strong>Domain:</strong> {currentConfig.domain}</p>
            <p><strong>Disabled Components:</strong> {
              currentConfig.disabledComponents.length > 0
                ? currentConfig.disabledComponents.join(", ")
                : "None"
            }</p>
          </div>
        </div>
      )}

      {/* Bulk Configuration Mode UI */}
      {courseOrder.length > 0 && bulkMode && (
        <div className={styles.sectionContainer}>
          <div className={styles.bulkHeader}>
            <h2>Bulk Configuration</h2>
            <p>Select multiple sections and apply the same configuration to all of them.</p>
          </div>

          <div className={styles.bulkLayout}>
            {/* Section Selection Panel */}
            <div className={styles.sectionSelectionPanel}>
              <div className={styles.sectionSelectionHeader}>
                <h3>Select Sections ({selectedSections.size} selected)</h3>
                <div className={styles.selectionButtons}>
                  <button onClick={selectAllSections} className={styles.smallButton}>
                    Select All
                  </button>
                  <button onClick={clearSectionSelection} className={styles.smallButton}>
                    Clear
                  </button>
                </div>
              </div>

              <div className={styles.sectionList}>
                {courseOrder.map((section, index) => {
                  const sectionId = section.Vocabulary_List || `section-${section.Identifier}-${index}`;
                  return (
                    <div
                      key={`${section.Identifier}-${index}`}
                      className={`${styles.sectionListItem} ${selectedSections.has(sectionId) ? styles.selectedSection : ''}`}
                      onClick={() => toggleSectionSelection(sectionId)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSections.has(sectionId)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSectionSelection(sectionId);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className={styles.sectionNumber}>{section.Identifier}</span>
                      <span className={styles.sectionName}>{section.Vocabulary_List || `Section ${section.Identifier}`}</span>
                      {courseConfigs[sectionId] && (
                        <span className={styles.configuredBadge}>Configured</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Configuration Panel */}
            <div className={styles.bulkConfigPanel}>
              <h3>Configuration to Apply</h3>

              {/* Domain Selection */}
              <div className={styles.domainSection}>
                <label className={styles.label}>Domain:</label>
                <select
                  value={bulkConfig.domain}
                  onChange={(e) => setBulkConfig(prev => ({ ...prev, domain: e.target.value }))}
                  className={styles.select}
                >
                  <option value="General">General</option>
                  <option value="Business">Business</option>
                  <option value="Medical">Medical</option>
                  <option value="Academic">Academic</option>
                  <option value="Travel">Travel</option>
                </select>
              </div>

              {/* Component Toggle Grid */}
              <div className={styles.componentGrid}>
                <h4>Disable/Enable Components:</h4>
                <div className={styles.grid}>
                  {AVAILABLE_COMPONENTS.map(component => (
                    <div key={component} className={styles.componentItem}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={bulkConfig.disabledComponents.includes(component)}
                          onChange={() => toggleBulkComponent(component)}
                        />
                        <span className={bulkConfig.disabledComponents.includes(component) ? styles.disabled : styles.enabled}>
                          {component} {bulkConfig.disabledComponents.includes(component) ? '(Disabled)' : '(Enabled)'}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes Section */}
              <div className={styles.notesSection}>
                <label className={styles.label}>Notes:</label>
                <textarea
                  value={bulkConfig.notes}
                  onChange={(e) => setBulkConfig(prev => ({ ...prev, notes: e.target.value }))}
                  className={styles.textarea}
                  placeholder="Add any notes about this bulk configuration..."
                  rows={3}
                />
              </div>

              {/* Save Button */}
              <button
                onClick={saveBulkConfiguration}
                disabled={saving || selectedSections.size === 0}
                className={styles.saveButton}
              >
                {saving ? "Saving..." : `Apply to ${selectedSections.size} Section${selectedSections.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          {/* Bulk Configuration Summary */}
          <div className={styles.summary}>
            <h4>Bulk Configuration Summary:</h4>
            <p><strong>Selected Sections:</strong> {selectedSections.size}</p>
            <p><strong>Language:</strong> {selectedLanguage}</p>
            <p><strong>Domain:</strong> {bulkConfig.domain}</p>
            <p><strong>Disabled Components:</strong> {
              bulkConfig.disabledComponents.length > 0
                ? bulkConfig.disabledComponents.join(", ")
                : "None"
            }</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCustomization;